import { useState, useCallback, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { AGREEMENTS } from '@/pages/auth/agreementContent';
import { forexApi, toBrokerAccount } from '@/services/forexApi';
import type { BrokerAccount } from '@/lib/brokerAccounts';
import EditProfileSheet, { type ProfileData } from './components/EditProfileSheet';

/* ── Types ── */
interface MenuItem {
  icon: string;
  iconColor: string;
  iconBg: string;
  title: string;
  desc: string;
  right?: 'tag' | 'toggle' | 'arrow';
  tagText?: string;
  tagColor?: 'green' | 'orange' | 'red' | 'gray';
  onClick?: () => void;
  toggleValue?: boolean;
  onToggle?: (v: boolean) => void;
}

/* ── Tag color map ── */
const tagStyles: Record<string, string> = {
  green: 'bg-green-50 text-green-600',
  orange: 'bg-amber-50 text-amber-600',
  red: 'bg-red-50 text-red-500',
  gray: 'bg-gray-50 text-gray-500',
};

/* ── Risk disclosure doc (inline) ── */
const RISK_ACK_VERSION = 'forex-risk-2025-05-01';
const RISK_DOC = {
  title: '风险告知',
  updatedAt: '2025 年 5 月 1 日',
  sections: [
    {
      heading: '量化交易风险提示',
      body: '外汇、期货等杠杆交易具有极高的市场风险，可能导致您损失全部本金甚至更多。AI 量化交易策略基于历史数据训练，过往回测表现不代表未来实际收益。\n\n在极端行情、流动性枯竭或技术故障情况下，自动交易系统可能产生不可预期的交易结果。请您在充分理解风险的基础上谨慎使用本平台服务。',
    },
    {
      heading: '市场风险',
      body: '外汇市场受地缘政治、经济数据、央行政策等多种因素影响，汇率可能在短时间内发生剧烈波动。杠杆交易会放大这种波动带来的盈亏，您可能面临超过初始保证金数额的损失。',
    },
    {
      heading: '技术风险',
      body: '尽管我们采用高可用架构和多重冗余机制，但网络延迟、服务器故障、API 异常等技术问题仍可能导致信号延迟、订单执行失败或重复下单。请您始终保持对账户的监控。',
    },
    {
      heading: 'AI 局限性',
      body: 'ForexMind AI 基于统计模型和历史模式识别生成交易信号，无法预测黑天鹅事件或前所未有的市场结构变化。AI 建议仅供参考，不构成投资顾问服务或交易指令。',
    },
    {
      heading: '合规提示',
      body: '请确保您的交易行为符合所在国家/地区的法律法规。部分国家/地区可能限制或禁止零售外汇杠杆交易。如有疑问，请咨询当地法律或财务顾问。',
    },
  ],
};

export default function ProfilePage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, signOut, updateUserProfile, accessToken } = useAuth();
  const { t, i18n } = useTranslation();

  /* ── User card states ── */
  const displayEmail = user?.email || '';
  const rawName = displayEmail ? displayEmail.split('@')[0] : '用户';

  const [profileData, setProfileData] = useState<ProfileData>({
    nickname: '',
    avatarUrl: null,
    avatarColor: '#6366f1',
  });
  const [showEditSheet, setShowEditSheet] = useState(false);

  const displayName = profileData.nickname || rawName;
  const initialChar = displayName.charAt(0).toUpperCase();
  const [notifyEnabled, setNotifyEnabled] = useState(true);
  const [signingOut, setSigningOut] = useState(false);
  const [showSignOutConfirm, setShowSignOutConfirm] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [showAccountClosureRequest, setShowAccountClosureRequest] = useState(false);
  const [showAbout, setShowAbout] = useState(false);
  const [showAgreement, setShowAgreement] = useState<number | null>(null);
  const [showLanguagePicker, setShowLanguagePicker] = useState(false);
  const [signingRisk, setSigningRisk] = useState(false);
  const riskSigned = user?.risk_ack_version === RISK_ACK_VERSION;

  useEffect(() => {
    setProfileData(prev => ({
      ...prev,
      nickname: user?.nickname || user?.user_metadata?.full_name || '',
      avatarUrl: user?.avatar_url || user?.user_metadata?.avatar_url || null,
    }));
  }, [user?.avatar_url, user?.nickname, user?.user_metadata?.avatar_url, user?.user_metadata?.full_name]);

  // ── Broker account status ──
  const [brokerAccounts, setBrokerAccounts] = useState<BrokerAccount[]>([]);
  const reviewingCount = brokerAccounts.filter(a => a.accountStatus === 'reviewing').length;
  const connectedCount = brokerAccounts.filter(a => a.accountStatus === 'connected').length;
  const releasedCount = brokerAccounts.filter(a => a.accountStatus === 'released').length;
  const isConnected = connectedCount > 0;
  const hasAny = brokerAccounts.length > 0;

  // ── Auto-open risk signing from homepage deep link ──
  useEffect(() => {
    const action = searchParams.get('action');
    if (action === 'risk-sign' && !riskSigned) {
      setShowAgreement(-1);
      // clean up the URL param
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('action');
      setSearchParams(newParams, { replace: true });
    }
  }, [riskSigned, searchParams, setSearchParams]);

  const refreshBrokerAccounts = useCallback(async () => {
    if (!accessToken) {
      setBrokerAccounts([]);
      return;
    }
    const accounts = await forexApi.listFollowerAccounts(accessToken);
    setBrokerAccounts(accounts.map(toBrokerAccount));
  }, [accessToken]);

  useEffect(() => {
    void refreshBrokerAccounts();
  }, [refreshBrokerAccounts]);

  // ── Refresh broker accounts when page gains focus ──
  useEffect(() => {
    const handleFocus = () => {
      void refreshBrokerAccounts();
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [refreshBrokerAccounts]);

  const handleSaveProfile = async (data: ProfileData) => {
    const nextUser = await updateUserProfile({
      nickname: data.nickname,
      avatarUrl: data.avatarUrl,
    });
    setProfileData(prev => ({
      ...prev,
      nickname: nextUser.nickname || data.nickname,
      avatarUrl: nextUser.avatar_url || data.avatarUrl,
    }));
  };

  const handleSignOut = async () => {
    setSigningOut(true);
    await signOut();
    setSigningOut(false);
    setShowSignOutConfirm(false);
  };

  const handleLanguageChange = (lang: string) => {
    i18n.changeLanguage(lang);
    setShowLanguagePicker(false);
  };

  const handleRiskSign = async () => {
    setSigningRisk(true);
    try {
      await updateUserProfile({ riskAckVersion: RISK_ACK_VERSION });
      setShowAgreement(null);
    } finally {
      setSigningRisk(false);
    }
  };

  /* ── Menu configs ── */
  const accountItems: MenuItem[] = [
    {
      icon: 'ri-link-m',
      iconColor: '#635BFF',
      iconBg: '#F0EEFF',
      title: '交易账户',
      desc: isConnected ? `已连接 ${connectedCount} · 审核中 ${reviewingCount} · 已释放 ${releasedCount}` : '绑定交易账户，开启智能跟单',
      right: 'tag',
      tagText: isConnected ? '已绑定' : '未绑定',
      tagColor: isConnected ? 'green' : 'orange',
      onClick: () => navigate('/broker'),
    },
  ];

  const securityItems: MenuItem[] = [
    { icon: 'ri-shield-keyhole-line', iconColor: '#F04438', iconBg: '#FFF0EF', title: '安全中心', desc: '2FA 登录保护', right: 'arrow', onClick: () => navigate('/security') },
    { icon: 'ri-user-unfollow-line', iconColor: '#6B7280', iconBg: '#F3F4F6', title: '账号注销申请', desc: '邮件申请，由人工核验处理', right: 'arrow', onClick: () => setShowAccountClosureRequest(true) },
  ];

  const prefItems: MenuItem[] = [
    { icon: 'ri-notification-3-line', iconColor: '#F79009', iconBg: '#FFF7E6', title: '消息中心', desc: '查看通知', right: 'arrow', onClick: () => navigate('/notifications') },
    { icon: 'ri-translate-2', iconColor: '#635BFF', iconBg: '#F0EEFF', title: '语言', desc: '简体中文 (English 即将上线)', right: 'arrow', onClick: () => setShowLanguagePicker(true) },
  ];

  const legalItems: MenuItem[] = [
    { icon: 'ri-error-warning-line', iconColor: '#F79009', iconBg: '#FFF7E6', title: '风险告知', desc: riskSigned ? '已签署' : '外汇跟单交易风险提示', right: riskSigned ? 'tag' : 'arrow', tagText: riskSigned ? '已签署' : undefined, tagColor: riskSigned ? 'green' : undefined, onClick: () => setShowAgreement(-1) },
    { icon: 'ri-file-text-line', iconColor: '#635BFF', iconBg: '#F0EEFF', title: '用户服务协议', desc: '服务条款与使用规范', right: 'arrow', onClick: () => setShowAgreement(0) },
    { icon: 'ri-shield-user-line', iconColor: '#12B76A', iconBg: '#E6F9F0', title: '隐私政策', desc: '数据收集与保护说明', right: 'arrow', onClick: () => setShowAgreement(1) },
    { icon: 'ri-customer-service-2-line', iconColor: '#635BFF', iconBg: '#F0EEFF', title: '帮助与支持', desc: '使用指南、联系客服', right: 'arrow', onClick: () => setShowHelp(true) },
    { icon: 'ri-information-line', iconColor: '#8A94A6', iconBg: '#F2F4F7', title: '关于 ForexMind', desc: '版本信息、产品介绍', right: 'arrow', onClick: () => setShowAbout(true) },
  ];

  /* ── Render helpers ── */
  const renderMenuGroup = useCallback((title: string, items: MenuItem[]) => (
    <div className="mt-5">
      <h3 className="text-[13px] text-gray-400 font-medium mb-2">{title}</h3>
      <div className="bg-white rounded-2xl overflow-hidden" style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
        {items.map((item, idx) => (
          <div
            key={item.title}
            onClick={item.right === 'toggle' ? undefined : item.onClick}
            className={`flex items-center gap-3 px-4 py-3.5 ${idx < items.length - 1 ? 'border-b border-gray-50' : ''} ${item.right !== 'toggle' && item.onClick ? 'cursor-pointer active:bg-gray-50' : ''}`}
          >
            <div className="w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0" style={{ background: item.iconBg }}>
              <i className={`${item.icon} text-lg`} style={{ color: item.iconColor }} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[15px] font-semibold text-gray-900">{item.title}</p>
              <p className="text-xs text-gray-400 mt-0.5">{item.desc}</p>
            </div>
            {item.right === 'tag' && item.tagText && (
              <span className={`text-xs font-medium px-2 py-0.5 rounded-full flex-shrink-0 ${tagStyles[item.tagColor || 'green']}`}>
                {item.tagText}
              </span>
            )}
            {item.right === 'toggle' && (
              <button
                onClick={(e) => { e.stopPropagation(); item.onToggle?.(!item.toggleValue); }}
                className={`w-12 h-7 rounded-full relative flex-shrink-0 transition-colors ${item.toggleValue ? 'bg-[#635BFF]' : 'bg-gray-200'}`}
              >
                <span className={`absolute top-[2px] w-6 h-6 bg-white rounded-full shadow-sm transition-all ${item.toggleValue ? 'right-[2px]' : 'left-[2px]'}`} />
              </button>
            )}
            {item.right === 'arrow' && (
              <i className="ri-arrow-right-s-line text-gray-300 text-lg flex-shrink-0" />
            )}
          </div>
        ))}
      </div>
    </div>
  ), []);

  /* ── Agreement doc selector ── */
  const agreementDoc = showAgreement === -1 ? RISK_DOC : showAgreement !== null ? AGREEMENTS[showAgreement] : null;
  const isRiskDoc = showAgreement === -1;

  return (
    <div className="min-h-screen bg-[#F4F6FA]">
      {/* ── Navbar ── */}
      <div className="bg-white border-b border-gray-100">
        <div className="flex items-center justify-between px-4 pb-3" style={{ paddingTop: 'max(env(safe-area-inset-top, 0px), 48px)' }}>
          <h1 className="text-xl font-bold text-gray-900">我的</h1>
          <button onClick={() => navigate('/notifications')} className="relative w-9 h-9 flex items-center justify-center">
            <i className="ri-notification-3-line text-gray-700 text-xl" />
            <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-red-500" />
          </button>
        </div>
      </div>

      {/* ── Scrollable content ── */}
      <div className="pt-4 pb-28 px-5">
        {/* User Card */}
        <div className="bg-white rounded-3xl p-5" style={{ boxShadow: '0 1px 6px rgba(0,0,0,0.04)' }}>
          <div className="flex items-center gap-4">
            {/* Avatar */}
            <div className="relative flex-shrink-0">
              {profileData.avatarUrl ? (
                <div
                  className="w-16 h-16 rounded-full bg-cover bg-center shadow-sm"
                  style={{ backgroundImage: `url(${profileData.avatarUrl})` }}
                />
              ) : (
                <div
                  className="w-16 h-16 rounded-full flex items-center justify-center shadow-sm"
                  style={{ background: profileData.avatarColor }}
                >
                  <span className="text-white text-2xl font-bold">{initialChar}</span>
                </div>
              )}
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0">
              <h2 className="text-base font-bold text-gray-900">{displayName}</h2>
              <p className="text-xs text-gray-400 mt-0.5 truncate">{displayEmail}</p>
            </div>

            {/* Edit profile button */}
            <button
              onClick={() => setShowEditSheet(true)}
              className="flex-shrink-0 px-4 py-2 rounded-full text-xs font-medium transition-colors active:bg-gray-100"
              style={{ background: '#F3F4F6', color: '#6B7280' }}
            >
              编辑资料
            </button>
          </div>
        </div>

        {/* Account binding prompt - prominent when not bound */}
        {!hasAny && (
          <div className="mt-5 bg-white rounded-2xl overflow-hidden" style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
            <div className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-amber-50 flex-shrink-0">
                  <i className="ri-link-unlink-m text-amber-500 text-xl" />
                </div>
                <div className="flex-1">
                  <p className="text-gray-900 font-bold text-sm">绑定交易账户</p>
                  <p className="text-gray-400 text-xs mt-0.5">绑定交易账户后，才能浏览智能体并开启自动跟单</p>
                </div>
              </div>
              <button
                onClick={() => navigate('/broker')}
                className="w-full mt-3 py-3 rounded-xl font-semibold text-sm flex items-center justify-center gap-2"
                style={{ background: 'linear-gradient(135deg, #6366F1, #7C3AED)', color: 'white' }}
              >
                <i className="ri-link-m" />
                立即绑定
              </button>
            </div>
            <div className="px-4 pb-3">
              <div className="flex items-center gap-2 text-gray-400 text-xs">
                <i className="ri-information-line" />
                <span>绑定流程：选择经纪商 → 填写账户信息 → 验证连接</span>
              </div>
            </div>
          </div>
        )}

        {/* Menu groups */}
        {renderMenuGroup('账户状态', accountItems)}
        {renderMenuGroup('安全与授权', securityItems)}
        {renderMenuGroup('偏好设置', prefItems)}
        {renderMenuGroup('法律与支持', legalItems)}

        {/* Sign out */}
        <div className="mt-6">
          <button
            onClick={() => setShowSignOutConfirm(true)}
            disabled={signingOut}
            className="w-full font-medium text-sm flex-shrink-0"
            style={{
              height: '52px',
              minHeight: '52px',
              borderRadius: '16px',
              background: '#FEF2F2',
              color: '#EF4444',
              border: 'none',
              padding: 0,
              WebkitAppearance: 'none',
              appearance: 'none',
              cursor: signingOut ? 'not-allowed' : 'pointer',
              display: 'block',
              position: 'relative',
            }}
          >
            <span className="absolute inset-0 flex items-center justify-center gap-2">
              {signingOut ? (
                <>
                  <div className="w-4 h-4 border-2 border-red-200 border-t-red-500 rounded-full animate-spin" />
                  退出中...
                </>
              ) : (
                <>
                  <i className="ri-logout-box-r-line" />
                  退出登录
                </>
              )}
            </span>
          </button>
        </div>
      </div>

      {/* ── Account closure request ── */}
      {showAccountClosureRequest && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.45)' }} onClick={() => setShowAccountClosureRequest(false)}>
          <div className="bg-white rounded-2xl mx-5 w-full max-w-[340px] p-5" role="dialog" aria-modal="true" aria-labelledby="account-closure-title" onClick={e => e.stopPropagation()}>
            <h3 id="account-closure-title" className="text-base font-bold text-gray-900">申请注销账号</h3>
            <p className="text-sm text-gray-600 mt-3 leading-relaxed">
              请发送邮件至 <span className="select-text break-all font-medium">zhoudaxing31@gmail.com</span>，说明要申请注销 ForexMind 账号。我们会核验身份，并核查跟随关系、未结交易及依法需要保留的记录后告知处理结果。
            </p>
            <p className="text-xs text-gray-500 mt-3 leading-relaxed">发出邮件不等于账号已注销；退出登录或解绑 Google 也不会注销账号。请勿在邮件中发送密码、验证码或交易账户凭据。</p>
            <div className="flex gap-3 mt-5">
              <button type="button" onClick={() => setShowAccountClosureRequest(false)} className="flex-1 h-11 rounded-xl bg-gray-100 text-gray-700 text-sm font-medium">关闭</button>
              <a href="mailto:zhoudaxing31@gmail.com?subject=ForexMind%20%E8%B4%A6%E5%8F%B7%E6%B3%A8%E9%94%80%E7%94%B3%E8%AF%B7" className="flex-1 h-11 rounded-xl bg-indigo-600 text-white text-sm font-medium flex items-center justify-center">打开邮件应用</a>
            </div>
          </div>
        </div>
      )}

      {/* ── Sign out confirm modal ── */}
      {showSignOutConfirm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.45)' }} onClick={() => setShowSignOutConfirm(false)}>
          <div className="bg-white rounded-2xl mx-5 w-full max-w-[300px] p-5" onClick={e => e.stopPropagation()}>
            <h3 className="text-base font-bold text-gray-900 text-center">确认退出登录？</h3>
            <p className="text-xs text-gray-500 text-center mt-2 leading-relaxed">退出后需要重新验证邮箱才能进入账户</p>
            <div className="flex gap-3 mt-5">
              <button
                onClick={() => setShowSignOutConfirm(false)}
                className="flex-1 font-medium text-sm flex-shrink-0"
                style={{
                  height: '44px',
                  minHeight: '44px',
                  borderRadius: '12px',
                  background: '#F3F4F6',
                  color: '#374151',
                  border: 'none',
                  padding: 0,
                  WebkitAppearance: 'none',
                  appearance: 'none',
                  display: 'block',
                  position: 'relative',
                }}
              >
                <span className="absolute inset-0 flex items-center justify-center">取消</span>
              </button>
              <button
                onClick={handleSignOut}
                className="flex-1 font-medium text-sm flex-shrink-0"
                style={{
                  height: '44px',
                  minHeight: '44px',
                  borderRadius: '12px',
                  background: '#FEF2F2',
                  color: '#EF4444',
                  border: 'none',
                  padding: 0,
                  WebkitAppearance: 'none',
                  appearance: 'none',
                  display: 'block',
                  position: 'relative',
                }}
              >
                <span className="absolute inset-0 flex items-center justify-center">退出登录</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Language Picker ── */}
      {showLanguagePicker && (
        <div className="fixed inset-0 z-[60] flex items-end" onClick={() => setShowLanguagePicker(false)}>
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
          <div className="relative bg-white rounded-t-3xl p-5 pb-8 w-full" style={{ maxWidth: '430px', margin: '0 auto' }} onClick={e => e.stopPropagation()}>
            <div className="w-10 h-1 rounded-full bg-gray-200 mx-auto mb-4" />
            <h2 className="text-gray-900 font-bold text-lg mb-4">选择语言</h2>
            <div className="flex flex-col gap-2">
              {[
                { code: 'zh', label: '简体中文', available: true },
                { code: 'en', label: 'English', available: false },
              ].map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => lang.available && handleLanguageChange(lang.code)}
                  disabled={!lang.available}
                  className={`flex items-center justify-between w-full px-4 py-3.5 rounded-xl transition-all ${
                    !lang.available
                      ? 'bg-gray-50 border border-gray-100 opacity-50 cursor-not-allowed'
                      : i18n.language === lang.code
                        ? 'bg-indigo-50 border border-indigo-200'
                        : 'bg-gray-50 border border-transparent'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-gray-900 text-sm font-medium">{lang.label}</span>
                    {!lang.available && (
                      <span className="text-[10px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded-full">即将上线</span>
                    )}
                  </div>
                  {i18n.language === lang.code && (
                    <div className="w-6 h-6 flex items-center justify-center rounded-full bg-indigo-500">
                      <i className="ri-check-line text-white text-xs" />
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Help full-screen overlay ── */}
      {showHelp && (
        <div className="fixed inset-0 z-[60] bg-[#F4F6FA] flex flex-col">
          <div className="bg-white border-b border-gray-100 flex-shrink-0" style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
            <div className="flex items-center justify-between px-4 h-12">
              <button onClick={() => setShowHelp(false)} className="w-8 h-8 flex items-center justify-center">
                <i className="ri-arrow-left-line text-gray-900 text-xl" />
              </button>
              <h2 className="text-base font-semibold text-gray-900">帮助与支持</h2>
              <div className="w-8" />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto px-5 py-5">
            <p className="text-gray-400 text-xs font-medium uppercase tracking-wide mb-3">常见问题</p>
            <div className="bg-white rounded-2xl overflow-hidden" style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
              {[
                { q: '如何开始跟随策略？', a: '选择平台 Agent，连接账户，阅读并授权当前策略，再查看实际就绪状态。', path: '/agent' },
                { q: '如何连接交易账户？', a: '支持范围以账户连接页当前可选平台和服务器为准。', path: '/broker' },
                { q: 'AI 智能体执行失败怎么办？', a: '可在智能体详情页查看执行日志，确认经纪商账户连接状态和策略配置是否正常。' },
                { q: '可以使用策略回测吗？', a: '当前尚未开放可使用的策略回测；已发生交易可在收益与历史记录中查看。', path: '/trade-history' },
                { q: '暂停、停止和释放有什么不同？', a: '暂停与停止的实际状态以我的跟随为准；停止不等于立即平仓，账户释放需另行处理。', path: '/agent?tab=my-copy' },
              ].map((item, idx, arr) => (
                <div key={idx} className={`px-4 py-3.5 ${idx < arr.length - 1 ? 'border-b border-gray-50' : ''}`}>
                  <p className="text-gray-800 text-sm font-medium mb-1.5">{item.q}</p>
                  <p className="text-gray-500 text-xs leading-relaxed">{item.a}</p>
                  {item.path && <button className="mt-2 text-xs text-indigo-600" onClick={() => { setShowHelp(false); navigate(item.path); }}>前往查看</button>}
                </div>
              ))}
            </div>

            <p className="text-gray-400 text-xs font-medium uppercase tracking-wide mb-3 mt-6">联系信息（可达性待核实）</p>
            <div className="flex flex-col gap-2.5">
              {[
                { icon: 'ri-mail-send-line', label: '邮件客服', value: 'support@forexmind.ai', color: '#635BFF' },
                { icon: 'ri-time-line', label: '服务时间', value: '周一至周五 09:00–18:00', color: '#12B76A' },
                { icon: 'ri-global-line', label: '在线文档', value: 'docs.forexmind.ai', color: '#3b82f6' },
              ].map(c => (
                <div key={c.label} className="flex items-center gap-3 bg-white rounded-xl px-4 py-3" style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0" style={{ background: `${c.color}15` }}>
                    <i className={`${c.icon} text-base`} style={{ color: c.color }} />
                  </div>
                  <div>
                    <p className="text-gray-400 text-xs">{c.label}</p>
                    <p className="text-gray-800 text-sm font-medium">{c.value}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="h-6" />
          </div>
        </div>
      )}

      {/* ── About full-screen overlay ── */}
      {showAbout && (
        <div className="fixed inset-0 z-[60] bg-[#F4F6FA] flex flex-col">
          <div className="bg-white border-b border-gray-100 flex-shrink-0" style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
            <div className="flex items-center justify-between px-4 h-12">
              <button onClick={() => setShowAbout(false)} className="w-8 h-8 flex items-center justify-center">
                <i className="ri-arrow-left-line text-gray-900 text-xl" />
              </button>
              <h2 className="text-base font-semibold text-gray-900">关于 ForexMind</h2>
              <div className="w-8" />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto px-5 py-6">
            {/* Logo */}
            <div className="flex flex-col items-center mb-6">
              <div className="w-20 h-20 flex items-center justify-center rounded-3xl mb-3" style={{ background: 'linear-gradient(135deg,#635BFF,#7c3aed)', boxShadow: '0 8px 24px rgba(99,102,241,0.3)' }}>
                <i className="ri-robot-2-fill text-white text-4xl" />
              </div>
              <h2 className="text-gray-900 font-bold text-xl">ForexMind</h2>
              <p className="text-gray-400 text-sm mt-0.5">AI 量化交易决策平台</p>
              <div className="flex items-center gap-2 mt-2">
                <span className="bg-indigo-50 text-indigo-500 text-xs px-2.5 py-0.5 rounded-full font-medium">v 1.0.0</span>
                <span className="bg-green-50 text-green-500 text-xs px-2.5 py-0.5 rounded-full font-medium">正式版</span>
              </div>
            </div>

            <div className="bg-white rounded-2xl overflow-hidden" style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
              {[
                { label: '当前版本', value: 'v 1.0.0', icon: 'ri-code-box-line', color: '#635BFF' },
                { label: '发布日期', value: '2025 年 5 月', icon: 'ri-calendar-line', color: '#12B76A' },
                { label: '支持平台', value: 'iOS / Android / Web', icon: 'ri-smartphone-line', color: '#3b82f6' },
                { label: 'AI 引擎', value: 'ForexMind GPT-Quant', icon: 'ri-cpu-line', color: '#8b5cf6' },
                { label: '数据提供', value: '全球主要外汇市场', icon: 'ri-global-line', color: '#F79009' },
              ].map((row, idx, arr) => (
                <div key={row.label} className={`flex items-center gap-3 px-4 py-3 ${idx < arr.length - 1 ? 'border-b border-gray-50' : ''}`}>
                  <div className="w-7 h-7 flex items-center justify-center rounded-lg flex-shrink-0" style={{ background: `${row.color}15` }}>
                    <i className={`${row.icon} text-sm`} style={{ color: row.color }} />
                  </div>
                  <span className="text-gray-400 text-xs flex-1">{row.label}</span>
                  <span className="text-gray-800 text-xs font-medium">{row.value}</span>
                </div>
              ))}
            </div>
            <p className="text-center text-gray-300 text-xs mt-6">© 2025 ForexMind Inc. All rights reserved.</p>
            <div className="h-6" />
          </div>
        </div>
      )}

      {/* ── Agreement viewer full-screen overlay ── */}
      {showAgreement !== null && agreementDoc && (
        <div className="fixed inset-0 z-[60] bg-[#F4F6FA] flex flex-col">
          <div className="bg-white border-b border-gray-100 flex-shrink-0" style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
            <div className="flex items-center justify-between px-4 h-12">
              <button onClick={() => setShowAgreement(null)} className="w-8 h-8 flex items-center justify-center">
                <i className="ri-arrow-left-line text-gray-900 text-xl" />
              </button>
              <h2 className="text-base font-semibold text-gray-900 truncate px-2">{agreementDoc.title}</h2>
              <div className="w-8" />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto px-5 py-5">
            <div className="bg-white rounded-2xl p-5" style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
              <p className="text-[#635BFF] text-xs font-medium mb-4">更新于 {agreementDoc.updatedAt}</p>
              {agreementDoc.sections.map((sec, i) => (
                <div key={i} className="mb-5">
                  <h4 className="text-gray-800 font-bold text-sm mb-2">{sec.heading}</h4>
                  <p className="text-gray-500 text-xs leading-relaxed" style={{ whiteSpace: 'pre-line' }}>{sec.body}</p>
                </div>
              ))}

              {/* ── Risk Signing Button ── */}
              {isRiskDoc && !riskSigned && (
                <div className="mt-5 pt-4 border-t border-gray-100">
                  <p className="text-gray-400 text-xs mb-3 text-center">请仔细阅读以上风险告知内容</p>
                  <button
                    onClick={handleRiskSign}
                    disabled={signingRisk}
                    className="w-full py-3.5 rounded-xl text-white font-semibold text-sm flex items-center justify-center gap-2 active:opacity-80 transition-opacity"
                    style={{ background: 'linear-gradient(135deg, #6366F1, #7C3AED)' }}
                  >
                    {signingRisk ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        签署中...
                      </>
                    ) : (
                      <>
                        <i className="ri-check-double-line" />
                        我已阅读并同意，确认签署
                      </>
                    )}
                  </button>
                </div>
              )}

              {isRiskDoc && riskSigned && (
                <div className="mt-5 pt-4 border-t border-gray-100">
                  <div className="flex items-center justify-center gap-2 text-green-600">
                    <i className="ri-shield-check-fill text-lg" />
                    <span className="text-sm font-medium">已完成风险签署</span>
                  </div>
                  <p className="text-gray-400 text-xs text-center mt-1">签署时间：{new Date().toLocaleDateString('zh-CN')}</p>
                </div>
              )}
            </div>
            <div className="h-6" />
          </div>
        </div>
      )}

      {/* ── Edit Profile Sheet ── */}
      <EditProfileSheet
        open={showEditSheet}
        profile={profileData}
        email={displayEmail}
        onSave={handleSaveProfile}
        onClose={() => setShowEditSheet(false)}
      />
    </div>
  );
}
